// Shared V2 list-page scaffold. Every CRUD surface (tenants, employees,
// buildings, etc.) renders as a page header + filter chips + a table.
// Pages pass column definitions + a fetcher; the scaffold owns the
// loading / error / empty states.

import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@ng-unifi/ui';
import { ApiError } from '../lib/api.js';
import { LoadingState } from './LoadingState.js';
import { useIsCompact } from '../lib/useIsCompact.js';
import type { FocusRowProps } from '../lib/useFocusParam.js';

export interface Column<T> {
  key: string;
  header: string;
  /** Cell renderer; defaults to `(row as any)[key]` as a string. */
  render?: (row: T) => ReactNode;
  width?: string;
}

export interface ResourceListProps<T> {
  title: string;
  subtitle?: string;
  /** Material Symbols icon name to render in the page-header chip. */
  icon?: string;
  /** Optional primary action button shown top-right. */
  action?: { label: string; icon?: string; onClick: () => void };
  /** Async loader. Throws ApiError → renders the error envelope inline. */
  load: () => Promise<T[]>;
  columns: Column<T>[];
  /** Stable row key, defaults to `(row as any).id`. */
  rowKey?: (row: T) => string;
  /** Empty-state copy. */
  emptyMessage?: string;
  /** Screen-reader text shown while rows are loading. */
  loadingLabel?: string;
  /**
   * UX-025/UX-030: when a row has a detail page, return its path here and the
   * first column becomes a real `<a href>`. A genuine link — not a `role="link"`
   * row — so middle-click, open-in-new-tab and copy-link-address all work, and
   * the table keeps its row semantics for screen readers.
   */
  rowHref?: (row: T) => string | null;
  /** UX-014: per-row props from `useFocusParam`, for `?focus=<id>` highlighting. */
  rowProps?: (row: T) => FocusRowProps;
}

function cellValue<T>(column: Column<T>, row: T): ReactNode {
  return column.render
    ? column.render(row)
    : String((row as unknown as Record<string, unknown>)[column.key] ?? '');
}

/** Wraps the primary cell in a real link when the row has a detail page. */
function primaryCell<T>(column: Column<T>, row: T, href: string | null): ReactNode {
  const content = cellValue(column, row);
  if (!href) return content;
  return (
    <Link
      to={href}
      className="rounded-sm text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      {content}
    </Link>
  );
}

export function ResourceList<T>({
  title,
  subtitle,
  icon,
  action,
  load,
  columns,
  rowKey,
  emptyMessage,
  loadingLabel,
  rowHref,
  rowProps,
}: ResourceListProps<T>) {
  const [rows, setRows] = useState<T[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const isCompact = useIsCompact();

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    load()
      .then((d) => {
        if (!cancelled) setRows(d);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load');
      });
    return () => {
      cancelled = true;
    };
    // load is captured by closure each render; surfaces pass a stable
    // module-level reference so this is fine to omit from deps.
  }, [load, reloadTick]);

  return (
    <div className="space-y-6">
      {/* flex-wrap + min-w-0: this header is shared by Hardware, Residents and
          others. At 320px the icon, title and action button cannot share a
          row, and the button is what has to move. */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          {icon ? (
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-container text-on-primary">
              <Icon name={icon} className="text-2xl" />
            </div>
          ) : null}
          <div className="min-w-0">
            <h1 className="font-headline-md text-headline-md text-on-surface">{title}</h1>
            {subtitle ? (
              <p className="font-body-md text-body-md text-on-surface-variant">{subtitle}</p>
            ) : null}
          </div>
        </div>
        {action ? (
          <button
            type="button"
            onClick={action.onClick}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary font-label-md text-label-md rounded-lg hover:bg-opacity-90 transition-all"
          >
            {action.icon ? <Icon name={action.icon} /> : null}
            <span>{action.label}</span>
          </button>
        ) : null}
      </header>

      <section className="bg-surface-container-lowest border border-outline-variant rounded-xl overflow-hidden">
        {error ? (
          // A read that failed is usually transient — a controller that did not
          // answer in time, a request that timed out. Without a retry here the
          // only way to try again is to navigate away and back, which is not
          // obvious and loses whatever else is on the page.
          <div className="space-y-3 p-6" role="alert">
            <p className="font-body-md text-body-md text-error">{error}</p>
            <button
              type="button"
              onClick={() => setReloadTick((t) => t + 1)}
              className="inline-flex items-center gap-2 rounded-md border border-outline-variant px-3 py-2 font-label-md text-label-md text-on-surface hover:bg-surface-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <Icon name="refresh" className="text-base" aria-hidden="true" />
              Try again
            </button>
          </div>
        ) : rows == null ? (
          <LoadingState label={loadingLabel} />
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-on-surface-variant font-body-md text-body-md">
            {emptyMessage ?? 'No records yet.'}
          </div>
        ) : isCompact ? (
          <ul className="divide-y divide-outline-variant">
            {rows.map((row) => {
              const k =
                rowKey?.(row) ?? (row as unknown as { id?: string }).id ?? JSON.stringify(row);
              const [primary, ...rest] = columns;
              const focus = rowProps?.(row);
              return (
                <li
                  key={k}
                  ref={focus?.ref}
                  data-focused={focus?.['data-focused']}
                  className={`space-y-3 p-4 ${focus?.className ?? ''}`}
                >
                  {primary ? (
                    <div className="font-title-sm text-title-sm text-on-surface">
                      {primaryCell(primary, row, rowHref?.(row) ?? null)}
                    </div>
                  ) : null}
                  {rest.length > 0 ? (
                    <dl className="space-y-1 font-body-md text-body-md">
                      {rest.map((c) => (
                        <div key={c.key} className="flex justify-between gap-3">
                          <dt className="text-on-surface-variant">{c.header}</dt>
                          <dd className="min-w-0 break-words text-right text-on-surface">
                            {cellValue(c, row)}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
                <thead className="bg-surface-container">
                  <tr>
                    {columns.map((c) => (
                      <th
                        key={c.key}
                        className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3"
                        style={c.width ? { width: c.width } : undefined}
                      >
                        {c.header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant">
                  {rows.map((row) => {
                    const k =
                      rowKey?.(row) ??
                      (row as unknown as { id?: string }).id ??
                      JSON.stringify(row);
                    const focus = rowProps?.(row);
                    const href = rowHref?.(row) ?? null;
                    return (
                      <tr
                        key={k}
                        ref={focus?.ref as ((node: HTMLTableRowElement | null) => void) | undefined}
                        data-focused={focus?.['data-focused']}
                        className={`hover:bg-surface-container-low transition-colors ${focus?.className ?? ''}`}
                      >
                        {columns.map((c, index) => (
                          <td
                            key={c.key}
                            className="px-4 py-3 font-body-md text-body-md text-on-surface"
                          >
                            {index === 0 ? primaryCell(c, row, href) : cellValue(c, row)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
          </div>
        )}
      </section>
    </div>
  );
}
